import { cn } from '@/lib/utils';

// Display-side superset of every challenge type the admin UI can render
// a badge for. This is intentionally wider than the API enum: it also
// covers the loose admin-only types (`trivia`, `location`, `task`,
// `scavenger`, `code`), the API `roadblock` type, and the DT-156
// minigame types. Keeping it a superset lets the narrower per-config
// unions (see `config/objects/{challenges,events}`) assign into the
// badge without casts, while the `Record` below enforces that every
// member has styling.
export type ChallengeType =
  | 'riddle'
  | 'trivia'
  | 'photo'
  | 'video'
  | 'location'
  | 'task'
  | 'puzzle'
  | 'scavenger'
  | 'code'
  | 'counting'
  | 'exploration'
  | 'search'
  | 'observation'
  | 'finale'
  | 'roadblock'
  // Minigames (DT-156)
  | 'slot_machine'
  | 'memory_match'
  | 'guess_word'
  | 'guess_song'
  | 'related_words';

interface ChallengeTypeBadgeProps {
  type: ChallengeType;
  className?: string;
  /** Use 'solid' variant for dark backgrounds */
  variant?: 'default' | 'solid';
}

/**
 * Challenge types grouped into five semantic families, each with ONE
 * muted tone — color communicates the *category* at a glance while the
 * label names the exact type. (Replaces the old one-hue-per-type
 * rainbow, which read as noise once a table had 6+ types.)
 */
const TYPE_FAMILIES = {
  /** Answered by thinking: riddles, trivia, puzzles, counts. */
  knowledge: {
    default: 'bg-indigo-100/70 text-indigo-700',
    solid: 'bg-indigo-500 text-white',
  },
  /** Proven with the camera. */
  media: {
    default: 'bg-rose-100/70 text-rose-700',
    solid: 'bg-rose-500 text-white',
  },
  /** Solved by going somewhere and looking. */
  field: {
    default: 'bg-emerald-100/70 text-emerald-700',
    solid: 'bg-emerald-600 text-white',
  },
  /** Played, not answered — the minigames. */
  minigame: {
    default: 'bg-fuchsia-100/70 text-fuchsia-700',
    solid: 'bg-fuchsia-500 text-white',
  },
  /** Milestones and staff-supervised moments. */
  milestone: {
    default: 'bg-amber-100/70 text-amber-700',
    solid: 'bg-amber-500 text-white',
  },
} as const;

type TypeFamily = keyof typeof TYPE_FAMILIES;

const typeConfig: Record<ChallengeType, { label: string; family: TypeFamily }> = {
  // `riddle` is the API enum value; `trivia` is the loose legacy type
  // some pre-enum rows still carry. They're the same thing to a player,
  // so both render as "Trivia".
  riddle: { label: 'Trivia', family: 'knowledge' },
  trivia: { label: 'Trivia', family: 'knowledge' },
  puzzle: { label: 'Puzzle', family: 'knowledge' },
  code: { label: 'Code', family: 'knowledge' },
  counting: { label: 'Counting', family: 'knowledge' },
  observation: { label: 'Observation', family: 'knowledge' },
  photo: { label: 'Photo', family: 'media' },
  video: { label: 'Video', family: 'media' },
  location: { label: 'Location', family: 'field' },
  exploration: { label: 'Exploration', family: 'field' },
  search: { label: 'Search', family: 'field' },
  scavenger: { label: 'Scavenger', family: 'field' },
  task: { label: 'Task', family: 'milestone' },
  finale: { label: 'Finale', family: 'milestone' },
  roadblock: { label: 'Roadblock', family: 'milestone' },
  slot_machine: { label: 'Slot Machine', family: 'minigame' },
  memory_match: { label: 'Memory Match', family: 'minigame' },
  guess_word: { label: 'Guess the Word', family: 'minigame' },
  guess_song: { label: 'Guess the Song', family: 'minigame' },
  related_words: { label: 'Related Words', family: 'minigame' },
};

export function ChallengeTypeBadge({ type, className, variant = 'default' }: ChallengeTypeBadgeProps) {
  const config = typeConfig[type];
  if (!config) return null;

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        TYPE_FAMILIES[config.family][variant],
        className,
      )}
    >
      {config.label}
    </span>
  );
}
